import * as React from "react";
import {
  Button,
  SearchInput,
  Sheet,
  SheetContent,
  cn,
  type DocsFolder,
  type DocsPage,
  type DocsSpace,
} from "@siena-ai/design-system";
import { FolderIconSafe as FolderIcon } from "./FolderIconSafe";
import { ChevronLeft, ChevronRight, FileText, Hash, Menu, MessageCircle } from "lucide-react";
import { PublicDocsSidebar } from "./PublicDocsSidebar";
import { ThemeToggle } from "./ThemeToggle";
import { ReaderMenu } from "./ReaderMenu";
import { DocsTableOfContents } from "./docs-table-of-contents";
import { ensureSearchIndex, runSearch, type SearchHit } from "../search";
import type { SiteManifest } from "../types";
import { openLiveChat } from "../lib/liveChat";

const HEADER_H = "3.5rem";

interface DocsSiteLayoutProps {
  space: DocsSpace;
  folders: DocsFolder[];
  pages: DocsPage[];
  manifest?: SiteManifest;
  currentPage?: DocsPage | null;
  currentFolder?: DocsFolder | null;
  prevPage?: DocsPage | null;
  nextPage?: DocsPage | null;
  pageHtmlContent?: string;
  onPageClick: (page: DocsPage) => void;
  onSearchHitClick?: (slug: string, anchor: string | null) => void;
  pageHref?: (page: DocsPage) => string;
  onLogoClick: () => void;
  onBreadcrumbFolderClick?: (folderId: string) => void;
  children: React.ReactNode;
}

// Mirrors DS DocsTableOfContents slugify so injected ids match TOC anchors.
function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .trim();
}

// Synthetic markdown for the local `DocsTableOfContents`, which takes
// markdown rather than a DOM.
//
// Each heading keeps its original level: H1 → `#`, H2 → `##`, H3 → `###`.
// The local ToC component uses relative indentation — the shallowest level
// found on the page is flush, and deeper headings indent progressively.
// This preserves hierarchy for toggle-block pages (H1 + H2 + H3) while
// staying backward-compatible with pages that only use H2 + H3.
//
// The node set must stay in step with `injectHeadingIds` below. Both slugify
// with the same duplicate counter, so indexing different sets of headings
// would hand the TOC anchors that no element carries.
export function htmlToTocMarkdown(html: string | undefined): string {
  if (!html || typeof window === "undefined") return "";
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const nodes = doc.querySelectorAll("h1, h2, h3");
    return Array.from(nodes)
      .map((n) => {
        const level = Number(n.tagName.slice(1));
        return `${"#".repeat(level)} ${n.textContent || ""}`;
      })
      .join("\n");
  } catch {
    return "";
  }
}

// Walks the given root and assigns deterministic ids to h1/h2/h3. Kept to the
// same headings `htmlToTocMarkdown` indexes — see the note there.
export function injectHeadingIds(root: HTMLElement | null) {
  if (!root) return;
  const seen = new Map<string, number>();
  const headings = root.querySelectorAll("h1, h2, h3");
  headings.forEach((el) => {
    const text = el.textContent?.trim() || "";
    if (!text) return;
    let id = slugify(text);
    const count = seen.get(id) ?? 0;
    seen.set(id, count + 1);
    if (count > 0) id = `${id}-${count}`;
    el.setAttribute("id", id);
  });
}

export const DocsSiteLayout: React.FC<DocsSiteLayoutProps> = ({
  space,
  folders,
  pages,
  manifest,
  currentPage,
  currentFolder,
  prevPage,
  nextPage,
  pageHtmlContent,
  onPageClick,
  onSearchHitClick,
  pageHref,
  onLogoClick,
  onBreadcrumbFolderClick,
  children,
}) => {
  const { layout } = space;
  const mainRef = React.useRef<HTMLDivElement | null>(null);

  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [searchFocused, setSearchFocused] = React.useState(false);
  const [searchHits, setSearchHits] = React.useState<SearchHit[] | null>(null);
  const [searchLoading, setSearchLoading] = React.useState(false);
  const [searchReady, setSearchReady] = React.useState(false);
  const searchRef = React.useRef<HTMLDivElement>(null);
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const searchReqRef = React.useRef(0);

  const containerClass = layout.showSidebar
    ? "max-w-[1280px]"
    : "max-w-[1000px]";

  // Preload the MiniSearch index on first focus so typing feels instant.
  const ensureIndex = React.useCallback(async () => {
    if (!manifest || searchReady) return;
    try {
      await ensureSearchIndex(manifest);
      setSearchReady(true);
    } catch {
      // Leave searchReady=false; the title-fallback below still works.
    }
  }, [manifest, searchReady]);

  // Fallback matcher — used when the MiniSearch index fails or is still loading.
  const titleFallback = React.useCallback(
    (q: string): SearchHit[] => {
      const query = q.trim().toLowerCase();
      if (!query) return [];
      return pages
        .filter(
          (p) =>
            p.status === "published" &&
            p.visibility === "public" &&
            (p.title.toLowerCase().includes(query) ||
              (p.description &&
                p.description.toLowerCase().includes(query)))
        )
        .slice(0, 8)
        .map((p) => {
          const folder = folders.find((f) => p.folderIds?.includes(f.id));
          return {
            id: p.id,
            pageUuid: p.id,
            pageSlug: p.slug,
            pageTitle: p.title,
            pageEmoji: p.emoji ?? null,
            folderName: folder?.name ?? null,
            sectionTitle: p.title,
            anchor: null,
            snippet: p.description ?? "",
            score: 1,
            isSection: false,
          };
        });
    },
    [pages, folders]
  );

  // Debounced search.
  React.useEffect(() => {
    const query = searchQuery.trim();
    if (!query) {
      setSearchHits(null);
      setSearchLoading(false);
      return;
    }

    const reqId = ++searchReqRef.current;
    setSearchLoading(true);

    const timer = window.setTimeout(async () => {
      try {
        if (!manifest) {
          if (reqId === searchReqRef.current) {
            setSearchHits(titleFallback(query));
            setSearchLoading(false);
          }
          return;
        }
        const mini = await ensureSearchIndex(manifest);
        if (reqId !== searchReqRef.current) return;
        const hits = runSearch(mini, query, 8);
        setSearchHits(hits.length > 0 ? hits : titleFallback(query));
        setSearchLoading(false);
        setSearchReady(true);
      } catch {
        if (reqId === searchReqRef.current) {
          setSearchHits(titleFallback(query));
          setSearchLoading(false);
        }
      }
    }, 120);

    return () => window.clearTimeout(timer);
  }, [searchQuery, manifest, titleFallback]);

  const handleHitClick = React.useCallback(
    (hit: SearchHit) => {
      setSearchQuery("");
      setSearchHits(null);
      setSearchFocused(false);
      // The hit buttons use onMouseDown + preventDefault so focus stays on
      // the input (avoids flicker). That's fine while the popover is open,
      // but after we route away the input is still DOM-focused while
      // searchFocused is false — which means the next keystroke would
      // update searchQuery without ever re-triggering onFocus, so the
      // popover would stay hidden until the user manually blurs/refocuses.
      // Blur explicitly so DOM focus and React state stay in sync.
      searchInputRef.current?.blur();
      if (onSearchHitClick) {
        onSearchHitClick(hit.pageSlug, hit.anchor);
      } else {
        const target = pages.find((p) => p.slug === hit.pageSlug);
        if (target) onPageClick(target);
      }
    },
    [onSearchHitClick, onPageClick, pages]
  );

  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchFocused(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  // Inject heading IDs into the rendered content so TOC anchors work.
  // Tiptap renders async, so also observe DOM changes and re-inject.
  React.useEffect(() => {
    const root = mainRef.current;
    if (!root) return;
    let raf = 0;
    const run = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => injectHeadingIds(root));
    };
    run();
    const observer = new MutationObserver(run);
    observer.observe(root, { childList: true, subtree: true });
    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
    };
  }, [currentPage?.id, pageHtmlContent]);

  const tocContent = React.useMemo(
    () => htmlToTocMarkdown(pageHtmlContent),
    [pageHtmlContent]
  );
  const showToc = Boolean(
    layout.showTableOfContents && pageHtmlContent && tocContent
  );

  const parentFolder = currentFolder?.parentFolderId
    ? folders.find((f) => f.id === currentFolder.parentFolderId)
    : null;

  return (
    <div
      className="min-h-screen bg-background"
      style={
        {
          "--brand-color": `#${space.brandColor}`,
        } as React.CSSProperties
      }
    >
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80">
        <div
          className={cn(
            "mx-auto flex items-center gap-8 h-14 px-6",
            containerClass
          )}
        >
          <div
            className={cn(
              "hidden lg:flex items-center shrink-0",
              layout.showSidebar ? "w-[260px] pl-4 pr-4" : ""
            )}
          >
            <button
              onClick={onLogoClick}
              className="flex items-center gap-2 hover:opacity-70 transition-opacity"
            >
              {space.branding.logoUrl ? (
                <img
                  src={space.branding.logoUrl}
                  alt={space.name}
                  width={121}
                  height={32}
                  className="max-w-[121px] max-h-8 object-contain dark:hidden"
                />
              ) : (
                <span className="text-sm font-semibold inline-flex items-center gap-1.5">
                  {space.emoji && (
                    <FolderIcon
                      emoji={space.emoji}
                      className="text-sm"
                      iconClassName="h-3.5 w-3.5"
                    />
                  )}
                  {space.name}
                </span>
              )}
              {space.branding.logoDarkUrl && (
                <img
                  src={space.branding.logoDarkUrl}
                  alt={space.name}
                  width={121}
                  height={32}
                  className="max-w-[121px] max-h-8 object-contain hidden dark:block"
                />
              )}
            </button>
          </div>

          <div className="lg:hidden flex items-center gap-2">
            {layout.showSidebar && (
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu />
              </Button>
            )}
            <button
              onClick={onLogoClick}
              className="flex items-center gap-1.5 hover:opacity-70 transition-opacity text-sm font-semibold"
            >
              {space.emoji && (
                <FolderIcon
                  emoji={space.emoji}
                  className="text-sm"
                  iconClassName="h-3.5 w-3.5"
                />
              )}
              {space.name}
            </button>
          </div>

          <div className="flex-1 flex items-center justify-end gap-2">
            <div ref={searchRef} className="relative">
              <SearchInput
                ref={searchInputRef}
                size="xs"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  // Defence-in-depth: if the user is typing, the popover
                  // should always be visible. Covers the case where the
                  // input kept DOM focus across a route change but
                  // searchFocused drifted to false, so onFocus wouldn't
                  // fire again on the next keystroke.
                  if (e.target.value && !searchFocused) {
                    setSearchFocused(true);
                    void ensureIndex();
                  }
                }}
                onFocus={() => {
                  setSearchFocused(true);
                  void ensureIndex();
                }}
                onClear={() => setSearchQuery("")}
                className="border-transparent bg-muted/40 hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:border-border/50"
                containerClassName="w-[160px] focus-within:w-[280px] transition-all duration-200"
              />
              {searchFocused && searchQuery.trim() && (
                <div className="absolute top-full right-0 mt-2 z-50 w-[420px] max-w-[calc(100vw-2rem)] rounded-xl border border-border/50 bg-card shadow-medium overflow-hidden">
                  {searchLoading && !searchHits && (
                    <div className="px-4 py-6 text-center text-xs text-muted-foreground">
                      Searching…
                    </div>
                  )}
                  {searchHits && searchHits.length > 0 && (
                    <div className="max-h-[70vh] overflow-y-auto py-1">
                      {searchHits.map((hit) => (
                        <button
                          key={hit.id}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleHitClick(hit);
                          }}
                          className="w-full text-left px-3.5 py-2.5 hover:bg-muted/50 transition-colors flex items-start gap-2.5"
                        >
                          <span className="mt-0.5 text-muted-foreground/70 shrink-0">
                            {hit.isSection ? (
                              <Hash className="h-3.5 w-3.5" />
                            ) : (
                              <FileText className="h-3.5 w-3.5" />
                            )}
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground/80 mb-0.5 truncate">
                              {hit.pageEmoji && (
                                <FolderIcon
                                  emoji={hit.pageEmoji}
                                  className="text-[11px]"
                                  iconClassName="h-3 w-3"
                                />
                              )}
                              <span className="truncate">
                                {hit.folderName
                                  ? `${hit.folderName} · ${hit.pageTitle}`
                                  : hit.pageTitle}
                              </span>
                            </div>
                            <div className="text-[13px] font-medium text-foreground truncate">
                              {hit.sectionTitle}
                            </div>
                            {hit.snippet && (
                              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                {hit.snippet}
                              </p>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {searchHits && searchHits.length === 0 && !searchLoading && (
                    <div className="px-4 py-6 text-center">
                      <p className="text-sm text-muted-foreground">
                        No results for "
                        <span className="text-foreground">{searchQuery}</span>"
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
            {space.linkedLiveChatId &&
              manifest?.space.liveChatScriptUrl &&
              manifest?.space.liveChatAppKey && (
                <Button
                  variant="outline"
                  size="xs"
                  onClick={() => {
                    openLiveChat().catch((err) => {
                      // eslint-disable-next-line no-console
                      console.warn(
                        "[DocsSiteLayout] openLiveChat failed",
                        err,
                      );
                    });
                  }}
                  className="shrink-0"
                >
                  <MessageCircle />
                  <span className="hidden sm:inline">
                    {space.chatButtonLabel || "Ask AI"}
                  </span>
                </Button>
              )}
            <ThemeToggle variant="ghost" size="icon-sm" />
            <ReaderMenu />
          </div>
        </div>
      </header>

      {layout.showSidebar && (
        <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
          <SheetContent
            side="left"
            size="sm"
            className="p-0 bg-background"
            hideCloseButton
          >
            <PublicDocsSidebar
              folders={folders}
              pages={pages}
              activePageId={currentPage?.id}
              pageHref={pageHref}
              onPageClick={(page) => {
                setSidebarOpen(false);
                onPageClick(page);
              }}
              activeFolderId={currentFolder?.id}
            />
          </SheetContent>
        </Sheet>
      )}

      <div className={cn("mx-auto flex gap-8 px-6", containerClass)}>
        {layout.showSidebar && (
          <PublicDocsSidebar
            className="hidden lg:block sticky shrink-0 w-[260px]"
            style={{ top: HEADER_H, height: `calc(100vh - ${HEADER_H})` }}
            folders={folders}
            pages={pages}
            activePageId={currentPage?.id}
            pageHref={pageHref}
            onPageClick={onPageClick}
            activeFolderId={currentFolder?.id}
          />
        )}

        <main className={cn("flex-1 min-w-0", !showToc && "max-w-3xl")}>
          <div
            ref={mainRef}
            className="pt-3.5 pb-10 sm:pb-12 lg:pb-16"
          >
            {layout.showBreadcrumbs && currentPage && (
              <nav className="flex items-center gap-1.5 text-xs text-muted-foreground mb-8">
                {layout.showHomepage ? (
                  <button
                    onClick={onLogoClick}
                    className="hover:text-foreground transition-colors"
                  >
                    {space.name}
                  </button>
                ) : (
                  // With the homepage layout option off, `/` has no dedicated
                  // landing — it just falls back to the generic folder list.
                  // Don't advertise it as a destination: render the space
                  // name as a plain label so users don't click into an
                  // undifferentiated index view.
                  <span>{space.name}</span>
                )}
                {parentFolder && (
                  <>
                    <ChevronRight className="h-3 w-3" />
                    {/* Folder breadcrumbs route to `/#folder-<id>` on the
                        homepage listing. With the homepage disabled that
                        destination isn't exposed, so degrade to labels. */}
                    {layout.showHomepage ? (
                      <button
                        onClick={() =>
                          onBreadcrumbFolderClick?.(parentFolder.id)
                        }
                        className="cursor-pointer hover:text-foreground transition-colors"
                      >
                        <span className="flex items-center gap-1">
                          <FolderIcon
                            emoji={parentFolder.emoji}
                            className="text-xs"
                            iconClassName="h-3 w-3"
                          />
                          {parentFolder.name}
                        </span>
                      </button>
                    ) : (
                      <span className="flex items-center gap-1">
                        <FolderIcon
                          emoji={parentFolder.emoji}
                          className="text-xs"
                          iconClassName="h-3 w-3"
                        />
                        {parentFolder.name}
                      </span>
                    )}
                  </>
                )}
                {currentFolder && (
                  <>
                    <ChevronRight className="h-3 w-3" />
                    {layout.showHomepage ? (
                      <button
                        onClick={() =>
                          onBreadcrumbFolderClick?.(currentFolder.id)
                        }
                        className="cursor-pointer hover:text-foreground transition-colors"
                      >
                        <span className="flex items-center gap-1">
                          <FolderIcon
                            emoji={currentFolder.emoji}
                            className="text-xs"
                            iconClassName="h-3 w-3"
                          />
                          {currentFolder.name}
                        </span>
                      </button>
                    ) : (
                      <span className="flex items-center gap-1">
                        <FolderIcon
                          emoji={currentFolder.emoji}
                          className="text-xs"
                          iconClassName="h-3 w-3"
                        />
                        {currentFolder.name}
                      </span>
                    )}
                  </>
                )}
                <ChevronRight className="h-3 w-3" />
                <span className="text-foreground font-medium truncate max-w-[200px]">
                  {currentPage.title}
                </span>
              </nav>
            )}

            {children}

            {layout.showPrevNext && currentPage && (prevPage || nextPage) && (
              <div className="mt-16 pt-8 border-t border-border/60">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {prevPage ? (
                    <a
                      href={pageHref?.(prevPage) ?? "#"}
                      onClick={(e) => {
                        e.preventDefault();
                        onPageClick(prevPage);
                      }}
                      className="group text-left p-4 rounded-lg border border-border/60 hover:border-border hover:bg-muted/30 transition-colors"
                    >
                      <div className="text-xs text-muted-foreground mb-1">
                        Previous
                      </div>
                      <div className="text-sm font-medium group-hover:text-foreground transition-colors flex items-center gap-1.5">
                        <ChevronLeft className="h-3.5 w-3.5 shrink-0" />
                        {prevPage.title}
                      </div>
                    </a>
                  ) : (
                    <div />
                  )}
                  {nextPage ? (
                    <a
                      href={pageHref?.(nextPage) ?? "#"}
                      onClick={(e) => {
                        e.preventDefault();
                        onPageClick(nextPage);
                      }}
                      className="group text-right p-4 rounded-lg border border-border/60 hover:border-border hover:bg-muted/30 transition-colors"
                    >
                      <div className="text-xs text-muted-foreground mb-1">
                        Next
                      </div>
                      <div className="text-sm font-medium group-hover:text-foreground transition-colors flex items-center justify-end gap-1.5">
                        {nextPage.title}
                        <ChevronRight className="h-3.5 w-3.5 shrink-0" />
                      </div>
                    </a>
                  ) : (
                    <div />
                  )}
                </div>
              </div>
            )}
          </div>
        </main>

        {showToc && (
          <DocsTableOfContents
            content={tocContent}
            className="hidden xl:block pt-5 pb-10"
            stickyOffset={HEADER_H}
          />
        )}
      </div>
    </div>
  );
};
